// Moderation Queue - Real-time report management
import React, { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    doc,
    updateDoc,
    deleteDoc,
    getDoc,
    addDoc,
    serverTimestamp
} from 'firebase/firestore';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Tabs,
    Tab,
    Chip,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Avatar,
    Divider,
    Alert,
    LinearProgress,
    Checkbox,
    Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';

const SEVERITY_COLORS = {
    selfHarm: '#D32F2F',
    harmful: '#F44336',
    harassment: '#FF5722',
    inappropriate: '#FF9800',
    spam: '#FFC107',
    other: '#9E9E9E'
};

const SEVERITY_LABELS = {
    selfHarm: 'Self Harm',
    harmful: 'Harmful',
    harassment: 'Harassment',
    inappropriate: 'Inappropriate',
    spam: 'Spam',
    other: 'Other'
};

const STATUS_FILTERS = ['pending', 'reviewed', 'resolved', 'escalated'];

const ModerationQueue = () => {
    const { user, getIdToken } = useAuth();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [statusCounts, setStatusCounts] = useState({
        pending: 0,
        reviewed: 0,
        resolved: 0,
        escalated: 0
    });
    const [selectedReport, setSelectedReport] = useState(null);
    const [actionDialog, setActionDialog] = useState({ open: false, type: null });
    const [actionNote, setActionNote] = useState('');
    const [selectedReports, setSelectedReports] = useState([]);
    const [contentPreview, setContentPreview] = useState(null);

    useEffect(() => {
        const unsubs = STATUS_FILTERS.map((status) => {
            const countQuery = query(
                collection(db, 'moderation_reports'),
                where('status', '==', status)
            );
            return onSnapshot(countQuery, (snapshot) => {
                setStatusCounts((prev) => ({ ...prev, [status]: snapshot.size }));
            }, () => {
                setStatusCounts((prev) => ({ ...prev, [status]: prev[status] || 0 }));
            });
        });
        return () => unsubs.forEach((unsub) => unsub());
    }, []);

    useEffect(() => {
        const status = STATUS_FILTERS[tabValue];
        setLoading(true);
        let unsubscribe = () => {};
        const attach = (ordered) => {
            const q = ordered
                ? query(
                    collection(db, 'moderation_reports'),
                    where('status', '==', status),
                    orderBy('createdAt', 'desc')
                )
                : query(
                    collection(db, 'moderation_reports'),
                    where('status', '==', status)
                );
            unsubscribe = onSnapshot(q, (snapshot) => {
                const reportsData = snapshot.docs.map(docSnap => ({
                    id: docSnap.id,
                    ...docSnap.data()
                }));
                reportsData.sort((a, b) => {
                    const ts = (item) => {
                        const t = item.createdAt;
                        if (!t) return 0;
                        if (typeof t.toMillis === 'function') return t.toMillis();
                        const parsed = new Date(t).getTime();
                        return Number.isFinite(parsed) ? parsed : 0;
                    };
                    return ts(b) - ts(a);
                });
                setReports(reportsData);
                setStatusCounts((prev) => ({ ...prev, [status]: reportsData.length }));
                setLoading(false);
            }, (error) => {
                console.error('Error loading reports:', error);
                if (ordered) {
                    unsubscribe();
                    attach(false);
                    return;
                }
                setReports([]);
                setLoading(false);
            });
        };
        attach(true);

        return () => unsubscribe();
    }, [tabValue]);

    const loadContentPreview = async (report) => {
        try {
            if (report.contentType === 'message' && report.roomId && report.messageId) {
                const msgDoc = await getDoc(
                    doc(db, 'chat_rooms', report.roomId, 'messages', report.messageId)
                );
                if (msgDoc.exists()) {
                    setContentPreview({ type: 'message', data: msgDoc.data() });
                } else {
                    setContentPreview({ type: 'message', data: { text: '[Message deleted or not found]' } });
                }
            } else if (report.contentType === 'forum_post' && report.contentId) {
                const postDoc = await getDoc(doc(db, 'forum_posts', report.contentId));
                if (postDoc.exists()) {
                    setContentPreview({ type: 'forum_post', data: postDoc.data() });
                } else {
                    setContentPreview({ type: 'forum_post', data: { content: '[Post deleted or not found]', title: '' } });
                }
            } else if (report.contentType === 'question' && report.questionId) {
                const qDoc = await getDoc(doc(db, 'qa_questions', report.questionId));
                if (qDoc.exists()) {
                    setContentPreview({ type: 'question', data: qDoc.data() });
                } else {
                    setContentPreview({ type: 'question', data: { text: '[Question deleted or not found]' } });
                }
            } else if (report.contentType === 'answer' && report.questionId && report.answerId) {
                const aDoc = await getDoc(
                    doc(db, 'qa_questions', report.questionId, 'answers', report.answerId)
                );
                if (aDoc.exists()) {
                    setContentPreview({ type: 'answer', data: aDoc.data() });
                } else {
                    setContentPreview({ type: 'answer', data: { text: '[Answer deleted or not found]' } });
                }
            } else {
                // Unknown content type or missing IDs — show report metadata so the modal still opens
                setContentPreview({ type: report.contentType || 'unknown', data: { content: report.reporterNote || '[No preview available]' } });
            }
        } catch (error) {
            console.error('Error loading content:', error);
            setContentPreview({ type: report.contentType || 'unknown', data: { content: '[Error loading content]' } });
        }
    };

    const handleViewReport = async (report) => {
        setSelectedReport(report);
        setContentPreview(null);
        await loadContentPreview(report);
    };

    // reportOverride lets callers pass the report directly to avoid async state read issues
    const handleAction = async (type, reportOverride) => {
        const report = reportOverride || selectedReport;
        if (!report) return;

        try {
            const reportRef = doc(db, 'moderation_reports', report.id);

            // Log the action
            await addDoc(collection(db, 'admin_audit_log'), {
                action: type,
                reportId: report.id,
                contentType: report.contentType,
                targetUserId: report.reportedUserId,
                performedBy: user.uid,
                note: actionNote,
                performedAt: serverTimestamp()
            });

            switch (type) {
                case 'ignore':
                    await updateDoc(reportRef, {
                        status: 'resolved',
                        resolution: 'ignored',
                        resolvedBy: user.uid,
                        resolvedAt: serverTimestamp()
                    });
                    break;

                case 'warn':
                    await updateDoc(reportRef, {
                        status: 'resolved',
                        resolution: 'warned',
                        resolvedBy: user.uid,
                        resolvedAt: serverTimestamp(),
                        note: actionNote || ''
                    });
                    if (report.reportedUserId) {
                        const warningBody = actionNote.trim()
                            ? actionNote.trim()
                            : 'A moderator reviewed content associated with your account. Please follow community guidelines.';
                        await addDoc(collection(db, 'user_notifications'), {
                            userId: report.reportedUserId,
                            title: 'Community guidelines reminder',
                            message: warningBody,
                            type: 'warning',
                            priority: 'high',
                            read: false,
                            delivered: false,
                            createdAt: serverTimestamp(),
                            reportId: report.id
                        });
                    }
                    break;

                case 'delete':
                    // Delete the reported content
                    if (report.contentType === 'message' && report.roomId && report.messageId) {
                        await deleteDoc(doc(db, 'chat_rooms', report.roomId, 'messages', report.messageId));
                    } else if (report.contentType === 'forum_post' && report.contentId) {
                        // Soft-delete: set deleted flag so app hides the post without losing moderation history
                        await updateDoc(doc(db, 'forum_posts', report.contentId), { deleted: true, deletedAt: serverTimestamp(), deletedBy: user.uid });
                    } else if (report.contentType === 'question' && report.questionId) {
                        await updateDoc(doc(db, 'qa_questions', report.questionId), { deleted: true });
                    }
                    await updateDoc(reportRef, {
                        status: 'resolved',
                        resolution: 'deleted',
                        resolvedBy: user.uid,
                        resolvedAt: serverTimestamp()
                    });
                    break;

                case 'ban': {
                    // Set banned claim via Cloud Function
                    const token = await getIdToken();
                    await fetch('https://us-central1-ovuloom-femtech.cloudfunctions.net/setAdminClaims', {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            targetUserId: report.reportedUserId,
                            role: 'banned'
                        })
                    });
                    await updateDoc(reportRef, {
                        status: 'resolved',
                        resolution: 'banned',
                        resolvedBy: user.uid,
                        resolvedAt: serverTimestamp()
                    });
                    break;
                }

                case 'escalate':
                    await updateDoc(reportRef, {
                        status: 'escalated',
                        escalatedBy: user.uid,
                        escalatedAt: serverTimestamp(),
                        escalationNote: actionNote
                    });
                    // For self-harm, prepare crisis email
                    if (report.reason === 'selfHarm') {
                        window.open(`mailto:crisis@ovuloom.com?subject=Urgent: Self-harm report escalation&body=Report ID: ${report.id}%0A%0ANote: ${actionNote}`);
                    }
                    break;

                default:
                    break;
            }

            setActionDialog({ open: false, type: null });
            setActionNote('');
            setSelectedReport(null);
            setContentPreview(null);
        } catch (error) {
            console.error('Error taking action:', error);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Unknown';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleString();
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold">
                        Moderation Queue
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Review and manage community reports
                    </Typography>
                </Box>
                <Box>
                    <Tooltip title="Refresh">
                        <IconButton><RefreshIcon /></IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Tabs */}
            <Tabs
                value={tabValue}
                onChange={(e, v) => setTabValue(v)}
                sx={{ mb: 3 }}
            >
                <Tab label={`Pending (${Number.isFinite(Number(statusCounts.pending)) ? Number(statusCounts.pending) : 0})`} />
                <Tab label={`Reviewed (${Number.isFinite(Number(statusCounts.reviewed)) ? Number(statusCounts.reviewed) : 0})`} />
                <Tab label={`Resolved (${Number.isFinite(Number(statusCounts.resolved)) ? Number(statusCounts.resolved) : 0})`} />
                <Tab label={`Escalated (${Number.isFinite(Number(statusCounts.escalated)) ? Number(statusCounts.escalated) : 0})`} />
            </Tabs>

            {loading ? (
                <LinearProgress />
            ) : reports.length === 0 ? (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <CheckCircleIcon sx={{ fontSize: 64, color: '#4ECDC4', mb: 2 }} />
                        <Typography variant="h6">All Clear!</Typography>
                        <Typography color="text.secondary">No {STATUS_FILTERS[tabValue]} reports</Typography>
                    </CardContent>
                </Card>
            ) : (
                <Box>
                    {reports.map((report) => (
                        <Card key={report.id} sx={{ mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    {/* Checkbox for bulk actions */}
                                    <Checkbox
                                        checked={selectedReports.includes(report.id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedReports([...selectedReports, report.id]);
                                            } else {
                                                setSelectedReports(selectedReports.filter(id => id !== report.id));
                                            }
                                        }}
                                    />

                                    {/* Report Info */}
                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Chip
                                                size="small"
                                                label={SEVERITY_LABELS[report.reason] || report.reason}
                                                sx={{
                                                    bgcolor: SEVERITY_COLORS[report.reason] || SEVERITY_COLORS.other,
                                                    color: 'white'
                                                }}
                                            />
                                            <Chip size="small" label={report.contentType} variant="outlined" />
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDate(report.createdAt)}
                                            </Typography>
                                        </Box>

                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Reporter note:</strong> {report.note || 'No details provided'}
                                        </Typography>

                                        {report.roomName && (
                                            <Typography variant="caption" color="text.secondary">
                                                Room: {report.roomName}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Actions */}
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Tooltip title="View Details">
                                            <IconButton onClick={() => handleViewReport(report)} color="primary">
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Ignore (mark resolved)">
                                            <IconButton
                                                onClick={() => handleAction('ignore', report)}
                                                color="success"
                                            >
                                                <CheckCircleIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete Content">
                                            <IconButton
                                                onClick={() => { setSelectedReport(report); setActionDialog({ open: true, type: 'delete' }); }}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                        {report.reason === 'selfHarm' && (
                                            <Tooltip title="Crisis Escalation">
                                                <IconButton
                                                    onClick={() => { setSelectedReport(report); setActionDialog({ open: true, type: 'escalate' }); }}
                                                    sx={{ color: '#D32F2F' }}
                                                >
                                                    <LocalHospitalIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}

            {/* Action Confirmation Dialog */}
            <Dialog open={actionDialog.open} onClose={() => setActionDialog({ open: false, type: null })}>
                <DialogTitle>
                    {actionDialog.type === 'delete' && 'Delete Content'}
                    {actionDialog.type === 'ban' && 'Ban User'}
                    {actionDialog.type === 'escalate' && 'Escalate to Crisis Team'}
                </DialogTitle>
                <DialogContent>
                    {actionDialog.type === 'escalate' && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            This will notify the crisis support team immediately.
                        </Alert>
                    )}
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Action Note (optional)"
                        value={actionNote}
                        onChange={(e) => setActionNote(e.target.value)}
                        placeholder="Add context for this action..."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setActionDialog({ open: false, type: null })}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleAction(actionDialog.type)}
                        variant="contained"
                        color={actionDialog.type === 'escalate' ? 'warning' : 'error'}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Content Preview Dialog */}
            <Dialog
                open={!!selectedReport && !!contentPreview}
                onClose={() => { setSelectedReport(null); setContentPreview(null); }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Content Preview</DialogTitle>
                <DialogContent>
                    {contentPreview && (
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {contentPreview.type.toUpperCase().replace('_', ' ')}
                            </Typography>
                            {/* Show title for forum posts and questions */}
                            {(contentPreview.data.title) && (
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                    {contentPreview.data.title}
                                </Typography>
                            )}
                            <Card variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                                <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {contentPreview.data.content || contentPreview.data.text || contentPreview.data.body || 'Content unavailable'}
                                </Typography>
                            </Card>
                            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Author: {contentPreview.data.username || contentPreview.data.senderName || contentPreview.data.authorName || 'Anonymous'}
                                </Typography>
                                {contentPreview.data.category && (
                                    <Typography variant="caption" color="text.secondary">
                                        Category: {contentPreview.data.category}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setSelectedReport(null); setContentPreview(null); }}>
                        Close
                    </Button>
                    <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => setActionDialog({ open: true, type: 'warn' })}
                    >
                        Warn User
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => setActionDialog({ open: true, type: 'delete' })}
                    >
                        Delete Content
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ModerationQueue;
